import { ArrowLeft } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import {
  MarketingPreferencesSection,
  SettingsSection,
  UserProfileCard,
  useSettingsConfig,
} from "./settings";

interface SettingsPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  /** Primary line: preferred / full name from profile, or auth name, or email local-part. */
  userDisplayName: string;
  userEmail: string;
  userInitials: string;
  userProfileImageUrl?: string;
  onNavigateToPasswordSecurity?: () => void;
  onNavigateToLanguageRegion?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacyPolicy?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToAccountDeletionPublic?: () => void;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
  /** Clears session (API logout + local state); same as menu "Sair". */
  onLogout?: () => void;
}

export function SettingsPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  userDisplayName,
  userEmail,
  userInitials,
  userProfileImageUrl,
  onNavigateToPasswordSecurity,
  onNavigateToLanguageRegion,
  onNavigateToAbout,
  onNavigateToTerms,
  onNavigateToPrivacyPolicy,
  onNavigateToPrivacy,
  onNavigateToAccountDeletionPublic,
  isDarkMode,
  onToggleDarkMode,
  onLogout,
}: SettingsPageProps) {
  const handleLogout = () => {
    if (!onLogout) return;
    if (!confirm("Tem certeza que deseja sair?")) return;
    onLogout();
  };

  const sections = useSettingsConfig({
    onNavigateToPasswordSecurity,
    onNavigateToLanguageRegion,
    onNavigateToAbout,
    onNavigateToTerms,
    onNavigateToPrivacyPolicy,
    onNavigateToPrivacy,
    onNavigateToAccountDeletionPublic,
    isDarkMode,
    onToggleDarkMode,
    onLogoutClick: onLogout ? handleLogout : undefined,
  });

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Configurações</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {/* User Profile Card */}
        <UserProfileCard
          name={userDisplayName}
          email={userEmail}
          initials={userInitials}
          imageUrl={userProfileImageUrl}
        />

        {/* Settings Sections */}
        <div className="px-4 space-y-6">
          {sections.map((section, index) => (
            <SettingsSection
              key={`${section.title}-${index}`}
              title={section.title}
              items={section.items}
            />
          ))}
          {/* PHASE_1 — Marketing opt-in/out (self-loading section). */}
          <MarketingPreferencesSection />
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}